import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { View } from '../../features/SavedViews/SavedViews.def';

interface ViewsState {
  views: View[];
  loading: boolean;
  error: string | null;
  /** ID of the currently active saved view (null = no view active) */
  activeViewId: number | null;
  /** Pre-view filters per component page, same shape as View.config.
   *  Auto-synced by useUrlFilters when no saved view is active.
   *  Frozen when a saved view is active, so it retains the pre-view state. */
  preViewFilters: Record<string, Record<string, string[]>>;
}

const initialState: ViewsState = {
  views: [],
  loading: false,
  error: null,
  activeViewId: null,
  preViewFilters: {},
};

export const viewsSlice = createSlice({
  name: 'views',
  initialState,
  reducers: {
    setViews: (state, action: PayloadAction<View[]>) => {
      state.views = action.payload;
    },
    setViewsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setViewsError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    addView: (state, action: PayloadAction<View>) => {
      state.views = [action.payload, ...state.views];
    },
    removeView: (state, action: PayloadAction<number>) => {
      state.views = state.views.filter((v) => v.id !== action.payload);
      if (state.activeViewId === action.payload) {
        state.activeViewId = null;
      }
    },
    setActiveViewId: (state, action: PayloadAction<number | null>) => {
      state.activeViewId = action.payload;
    },
    /** Store current filters for a component page (auto-called by useUrlFilters) */
    setPreViewFilters: (state, action: PayloadAction<{ component: string; config: Record<string, string[]> }>) => {
      // Merge keys — multiple hooks on the same page contribute different filter keys
      state.preViewFilters[action.payload.component] = {
        ...(state.preViewFilters[action.payload.component] || {}),
        ...action.payload.config,
      };
    },
    /** Clear active view ID (preViewFilters preserved for restore) */
    clearActiveView: (state) => {
      state.activeViewId = null;
    },
  },
});

export const {
  setViews,
  setViewsLoading,
  setViewsError,
  addView,
  removeView,
  setActiveViewId,
  setPreViewFilters,
  clearActiveView,
} = viewsSlice.actions;

export default viewsSlice.reducer;
